import { MenuItemType, PageTypeSuffix } from '@/constants'
import type { SubMenu } from '@/types'
import { type Menu } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { Tab } from '@headlessui/react'
import Link from 'next/link'
import { useState } from 'react'
import ImageOptimize from '../ImageOptimize'
interface MegaMenuProps {
  placeholder?: string
  className?: string
  isError?: boolean | string
  id?: string
  name?: string
  children?: JSX.Element[] | JSX.Element
  alert?: boolean
  value?: string
  icon?: JSX.Element
  megaMenuData: Menu[]
  closeModal: () => void
}

export const MegaMenu = ({ megaMenuData, closeModal }: MegaMenuProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const checkUrlType = (item: Menu | SubMenu) => {
    switch (item.type) {
      case MenuItemType.PRODUCT_CAT:
        return '/' + item.slug + PageTypeSuffix.CATEGORY
      case MenuItemType.POST_CAT:
        return '/' + item.slug + PageTypeSuffix.CATEGORY
      case MenuItemType.POST:
        return '/' + item.slug + PageTypeSuffix.POST
      case MenuItemType.PAGE_CMS:
        return '/' + item.slug + PageTypeSuffix.PAGE_CMS
      case MenuItemType.PRODUCT:
        return '/' + item.slug
      case MenuItemType.LINK:
        return item.url
      default:
        return '/'
    }
  }

  const renderIconGrid = (items: SubMenu[]) => (
    <div className='main grid grid-cols-3 gap-3 pt-3 px-2'>
      {items.map((child, index) => {
        return (
          <Link
            key={index}
            onClick={closeModal}
            href={checkUrlType(child) || '/'}
            className={'outline-0'}
          >
            <div className='flex flex-col justify-center'>
              {/* Chỉ render icon khi mục menu thực sự có icon. Trước đây rơi
                  về systemConfig.imagePlaceholder: menu nguyenkim không mục
                  cấp 1 nào có icon (0/63) nên hiện 7 logo mặc định giống hệt
                  nhau, còn môi trường có imagePlaceholder rỗng thì ra ảnh vỡ
                  (src="/?format=webp&quality=..." - tải lại chính trang). */}
              {child.icon && (
                <span className='icon px-2'>
                  <ImageOptimize
                    src={child.icon}
                    alt={getTranslate(child.name)}
                    width={120}
                    height={120}
                    thumbnail
                    loading='lazy'
                  />
                </span>
              )}
              <span className='line-clamp-2 h-[28px] text-center block overflow-hidden text-ellipsis text-textSmall'>
                {getTranslate(child.name)}
              </span>
            </div>
          </Link>
        )
      })}
    </div>
  )

  return (
    <>
      <Tab.Group
        vertical
        selectedIndex={selectedIndex}
        onChange={setSelectedIndex}
        as={'div'}
        className={'flex justify-between'}
      >
        <Tab.List
          className={
            'max-w-[148px] flex-[148px] gap-1 h-[calc(100svh-135px)] hiddenScrollBar overflow-y-auto pb-8 bg-[#F3F4F6]'
          }
        >
          {megaMenuData
            .sort((a, b) => (a.order > b.order ? 1 : -1))
            .map((e, i) => {
              return (
                <Tab
                  key={i}
                  as={'div'}
                  className={
                    'tabItemMega ui-selected:border-l-[3px] ui-selected:font-semibold ui-selected:border-l-[#F00000] ui-selected:bg-[#FFF0F0] rounded-br-[3px] rounded-tr-[3px] bg-white-500 text-center text-mainBody outline-0 border-b border-b-[#E4E4E4]'
                  }
                >
                  <div className='relative flex flex-col justify-center px-2 py-1 min-h-[50px]'>
                    <div className='ui-selected:block absolute right-0 top-[50%] z-10 hidden h-0 w-0 translate-y-[-50%] border-b-[8px] border-r-[12px] border-t-[8px] border-b-transparent border-r-white border-t-transparent'></div>
                    
                    <div className='title text-sm leading-4 flex text-left w-full'>
                      {getTranslate(e.name)}
                    </div>
                  </div>
                </Tab>
              )
            })}
        </Tab.List>
        <Tab.Panels
          className={
            'flex-1 px-2 max-h-[calc(100vh-135px)] hiddenScrollBar overflow-y-auto pb-8'
          }
        >
          {megaMenuData
            .sort((a, b) => (a.order > b.order ? 1 : -1))
            .map((e, i) => {
              return (
                <Tab.Panel key={i}>
                  <div className='flex flex-col flex-wrap'>
                    <div className='header my-[16px] flex w-full items-center justify-between'>
                      <h6 className='title text-sm font-semibold'>
                        {getTranslate(e.name)}
                      </h6>
                    </div>
                    
                    {/* Menu 2 cấp (Pico): cấp 2 là ô ảnh. Menu 3 cấp
                        (Nguyễn Kim): cấp 2 là nhóm không có icon, ảnh nằm ở
                        cấp 3 - vẽ nhóm thành tiêu đề + các mục cấp 3, tránh
                        ô ảnh vỡ do icon rỗng. */}
                    {(() => {
                      const children = e.children.sort((a, b) =>
                        a.order > b.order ? 1 : -1
                      )
                      const leaves = children.filter(c => !c.children?.length)
                      const groups = children.filter(c => c.children?.length)
                      return (
                        <div className='pb-20'>
                          {leaves.length > 0 && renderIconGrid(leaves)}
                          {groups.map((group, groupIndex) => {
                            const items = group.children.sort((a, b) =>
                              a.order > b.order ? 1 : -1
                            )
                            const hasIcon = items.some(item => item.icon)
                            return (
                              <div key={groupIndex} className='mb-4'>
                                <h6 className='px-2 text-sm font-semibold'>
                                  {group.url || group.type !== MenuItemType.LINK ? (
                                    <Link
                                      onClick={closeModal}
                                      href={checkUrlType(group) || '/'}
                                      className='outline-0'
                                    >
                                      {getTranslate(group.name)}
                                    </Link>
                                  ) : (
                                    getTranslate(group.name)
                                  )}
                                </h6>
                                {hasIcon ? (
                                  renderIconGrid(items)
                                ) : (
                                  <div className='grid grid-cols-2 gap-x-3 gap-y-2 pt-2 px-2'>
                                    {items.map((item, index) => (
                                      <Link
                                        key={index}
                                        onClick={closeModal}
                                        href={checkUrlType(item) || '/'}
                                        className='line-clamp-2 outline-0 text-textSmall'
                                      >
                                        {getTranslate(item.name)}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </div>
                </Tab.Panel>
              )
            })}
        </Tab.Panels>
      </Tab.Group>
    </>
  )
}
